import { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
} from '@heroui/react'

import {
  RecommendationLogsService,
  type RecommendationLogEntry,
} from '../services'
import { useToast } from '../../../hooks'

import { RecommendationLogItem } from './RecommendationLogItem'

export function RecommendationLogsViewer() {
  const [logs, setLogs] = useState<RecommendationLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [successFilter, setSuccessFilter] = useState<string>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [testRunFilter, setTestRunFilter] = useState<string>('production') // 'all', 'production', 'test'

  const ITEMS_PER_PAGE = 20
  const toast = useToast()

  const loadLogs = useCallback(
    async (offset = 0, append = false) => {
      setIsLoading(true)
      setError(null)

      try {
        const options: Parameters<typeof RecommendationLogsService.getLogs>[0] =
          {
            limit: ITEMS_PER_PAGE,
            offset,
          }

        // Apply filters
        if (successFilter !== 'all') {
          options.success = successFilter === 'success'
        }
        if (startDate) {
          options.startDate = new Date(startDate)
        }
        if (endDate) {
          // Set to end of day
          const endDateTime = new Date(endDate)
          endDateTime.setHours(23, 59, 59, 999)
          options.endDate = endDateTime
        }

        // Apply test run filter
        if (testRunFilter === 'production') {
          options.isTestRun = false
        } else if (testRunFilter === 'test') {
          options.isTestRun = true
        }
        // If 'all', don't set isTestRun filter

        const result = await RecommendationLogsService.getLogs(options)

        if (!result.success || !result.data) {
          throw new Error(result.error || 'Failed to load logs')
        }

        const newLogs = result.data.logs
        setTotalCount(result.data.totalCount)
        setHasMore(result.data.hasMore)
        setCurrentOffset(offset)

        if (append) {
          setLogs((prev) => [...prev, ...newLogs])
        } else {
          setLogs(newLogs)
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error'
        setError(errorMessage)
        toast.error(`Failed to load logs: ${errorMessage}`)
      } finally {
        setIsLoading(false)
      }
    },
    [successFilter, startDate, endDate, testRunFilter, toast]
  )

  const handleLoadMore = () => {
    const newOffset = currentOffset + ITEMS_PER_PAGE
    loadLogs(newOffset, true)
  }

  const handleFilterChange = () => {
    setCurrentOffset(0)
    loadLogs(0, false)
  }

  // Get stats for display
  const successCount = logs.filter((log) => log.success).length
  const failureCount = logs.filter((log) => !log.success).length
  const successRate = logs.length > 0 ? (successCount / logs.length) * 100 : 0

  // Load initial data
  useEffect(() => {
    loadLogs(0, false)
  }, [loadLogs])

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <h3 className="text-lg font-semibold">Overview</h3>
            <Button
              size="sm"
              variant="flat"
              onPress={() => loadLogs(0, false)}
              isLoading={isLoading}
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {totalCount.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Total Requests
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {successCount.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Successful
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {failureCount.toLocaleString()}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Failed
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {successRate.toFixed(1)}%
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Success Rate
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Filters</h3>
        </CardHeader>
        <CardBody>
          <div className="grid gap-4 md:grid-cols-5">
            <Select
              label="Status"
              placeholder="All requests"
              selectedKeys={[successFilter]}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string
                setSuccessFilter(value)
                setTimeout(handleFilterChange, 100)
              }}
            >
              <SelectItem key="all">All requests</SelectItem>
              <SelectItem key="success">Successful only</SelectItem>
              <SelectItem key="failure">Failed only</SelectItem>
            </Select>

            <Select
              label="Request Type"
              placeholder="Production only"
              selectedKeys={[testRunFilter]}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string
                setTestRunFilter(value)
                setTimeout(handleFilterChange, 100)
              }}
            >
              <SelectItem key="production">Production only</SelectItem>
              <SelectItem key="test">Test runs only</SelectItem>
              <SelectItem key="all">All requests</SelectItem>
            </Select>

            <Input
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                setTimeout(handleFilterChange, 100)
              }}
            />

            <Input
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                setTimeout(handleFilterChange, 100)
              }}
            />

            <div className="flex items-end">
              <Button
                variant="flat"
                onPress={() => {
                  setSuccessFilter('all')
                  setStartDate('')
                  setEndDate('')
                  setTimeout(handleFilterChange, 100)
                }}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Results Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Showing {logs.length} of {totalCount.toLocaleString()} requests
          </span>
          {(successFilter !== 'all' || startDate || endDate) && (
            <Chip size="sm" variant="flat" color="secondary">
              Filtered
            </Chip>
          )}
        </div>
        {isLoading && <Spinner size="sm" />}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950 dark:border-red-800">
          <div className="flex items-start gap-2">
            <div className="text-red-600 dark:text-red-400 mt-1">❌</div>
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200">
                Error Loading Logs
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="space-y-4">
        {logs.length === 0 && !isLoading && (
          <Card>
            <CardBody className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                No logs found
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                {successFilter !== 'all' || startDate || endDate
                  ? 'Try adjusting your filters to see more results.'
                  : 'No recommendation requests have been logged yet.'}
              </p>
            </CardBody>
          </Card>
        )}

        {logs.map((log) => (
          <RecommendationLogItem key={log.id} log={log} />
        ))}

        {/* Load More Button */}
        {hasMore && !isLoading && (
          <div className="text-center">
            <Button variant="flat" onPress={handleLoadMore}>
              Load More ({(totalCount - logs.length).toLocaleString()}{' '}
              remaining)
            </Button>
          </div>
        )}

        {isLoading && logs.length === 0 && (
          <Card>
            <CardBody className="text-center py-12">
              <Spinner size="lg" />
              <p className="mt-4 text-slate-600 dark:text-slate-400">
                Loading logs...
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  )
}
